import DOMPurify from 'dompurify';

const RICH_FORBID_TAGS = [
  'script',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'link',
  'meta',
  'base',
  'foreignObject',
  'use',
  'animate',
  'animateMotion',
  'animateTransform',
  'set',
];

const UNSAFE_CSS_PATTERN = /@import|expression\s*\(|behavior\s*:|-moz-binding|javascript\s*:|vbscript\s*:/gi;
const CSS_ESCAPE_PATTERN = /\\[0-9a-f]{1,6}\s?|\\(?=[^\n])/gi;
const CSS_URL_PATTERN = /url\(\s*(['"]?)(.*?)\1\s*\)/gi;
const SAFE_CSS_URL = /^(?:https:\/\/|data:image\/(?:png|jpe?g|gif|webp);|#|\/(?!\/)|\.{1,2}\/|[^:]*$)/i;

/** Quita del CSS lo que permite ejecutar código o cargar recursos de esquemas no seguros. */
export const sanitizeCss = (css: string): string =>
  css
    .replace(CSS_ESCAPE_PATTERN, '')
    .replace(UNSAFE_CSS_PATTERN, '')
    .replace(CSS_URL_PATTERN, (match, _quote: string, url: string) => (SAFE_CSS_URL.test(url.trim()) ? match : 'none'));

/** Si pegan un documento completo, conserva los <style> del <head> junto al contenido del <body>. */
const flattenHtmlDocument = (html: string): string => {
  if (!/<(?:html|head|body)[\s>]/i.test(html) || typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const headStyles = Array.from(doc.head.querySelectorAll('style'))
    .map((style) => style.outerHTML)
    .join('');
  return headStyles + doc.body.innerHTML;
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Convierte texto plano con saltos de línea a HTML básico si no hay etiquetas. */
export const normalizeRichContent = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/<[a-z][\s\S]*>/i.test(trimmed)) return trimmed;
  return trimmed
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br />')}</p>`)
    .join('');
};

/**
 * HTML de páginas y secciones de contenido: admite estilos, clases e íconos SVG en línea;
 * nunca scripts, manejadores de eventos, iframes ni formularios.
 */
export const sanitizeRichHtml = (dirty: string): string => {
  const normalized = normalizeRichContent(flattenHtmlDocument(dirty));
  if (!normalized) return '';

  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName === 'style' && node.textContent) {
      node.textContent = sanitizeCss(node.textContent);
    }
  });
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (!(node instanceof Element)) return;
    const inlineStyle = node.getAttribute('style');
    if (inlineStyle) node.setAttribute('style', sanitizeCss(inlineStyle));
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });

  try {
    return DOMPurify.sanitize(normalized, {
      USE_PROFILES: { html: true, svg: true },
      ADD_TAGS: ['style'],
      ADD_ATTR: ['target'],
      FORBID_TAGS: RICH_FORBID_TAGS,
      ALLOW_DATA_ATTR: false,
      FORCE_BODY: true,
    });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
    DOMPurify.removeHook('uponSanitizeElement');
  }
};

const FOOTER_URI_REGEXP = /^(?:(?:https?|mailto|tel|sms|whatsapp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;
const GOOGLE_MAPS_EMBED_PREFIX = 'https://www.google.com/maps/embed';

/** HTML completo para el footer: admite estilos propios y mapas de Google embebidos, nunca scripts. */
export const sanitizeFooterHtml = (dirty: string): string => {
  if (!dirty.trim()) return '';

  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName !== 'iframe') return;
    const src = (node as Element).getAttribute?.('src')?.trim() ?? '';
    if (!src.startsWith(GOOGLE_MAPS_EMBED_PREFIX)) {
      node.parentNode?.removeChild(node);
    }
  });
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node instanceof Element && node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });

  try {
    return DOMPurify.sanitize(dirty, {
      USE_PROFILES: { html: true },
      ADD_TAGS: ['style', 'iframe'],
      ADD_ATTR: ['target', 'style', 'class', 'allowfullscreen', 'loading', 'referrerpolicy'],
      FORBID_TAGS: ['script', 'object', 'embed', 'form'],
      ALLOWED_URI_REGEXP: FOOTER_URI_REGEXP,
      FORCE_BODY: true,
    });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
    DOMPurify.removeHook('uponSanitizeElement');
  }
};
